/**
 * Renders a preview of the Redemittel phrase list for one exam, inside a blog post.
 *
 * WHY THIS EXISTS
 * The nine *-redemittel-schreiben posts rank #4-9 for ~40 "redemittel" queries
 * and produce most of the site's search traffic, but none of them contained a
 * single phrase — they argued ABOUT Redemittel and linked a PDF. Someone typing
 * "redemittel schreiben b1 pdf" wants German phrases on the page. The competitors
 * that outrank us are Scribd scans and Studocu uploads whose only advantage is
 * that they contain the phrases.
 *
 * The data has been in the repo the whole time, behind auth at
 * /:lang/practice/redemittel. This component puts it on the public page.
 *
 * HOW MUCH IS PUBLIC
 * The first PREVIEW_PER_PART phrases of every exam task, then a gate offering
 * the complete PDF or a free account. Capping per TASK rather than overall keeps
 * every part of the exam represented, so the page still shows the full shape of
 * what Schreiben and Sprechen ask for — it just doesn't hand over the whole set.
 *
 * WHAT STAYS PAID
 * The SRS trainer, the session queue, the progress tracking in
 * users/{uid}/redemittelProgress and the AI writing correction are untouched —
 * this component never renders an exercise.
 *
 * PRERENDERING
 * Data is fetched at runtime rather than imported, because BlogPostPage imports
 * all 24 post components statically: a static JSON import would put ~240 KB of
 * phrase data into the main bundle for every visitor on every route. The fetch
 * means scripts/prerender-playwright.js must wait for the data before capturing
 * the HTML, which is what the data-prerender-ready attribute below is for. It is
 * set only once rows are actually in the DOM, so a failed fetch times out the
 * build loudly instead of silently shipping a page with no phrases on it.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, ArrowRight, FileDown, Repeat } from 'lucide-react';
import { useLangParam } from '../../hooks/useLocalizedNavigate';

/**
 * How many phrases each exam task shows before the rest is gated.
 *
 * Every task is represented, so the page still demonstrates the full shape of
 * the exam rather than stopping partway through Schreiben. The remainder goes
 * behind the PDF (which generate_redemittel_pdf.py builds from every card in
 * every part, so it genuinely is the complete set) or a free account.
 */
const PREVIEW_PER_PART = 10;

/**
 * Count cards from the parts themselves, never from metadata.totalCards.
 * The metadata block is stale in several files (telc-b1 claims 78 cards and
 * 16 in teil3_planen; the parts actually hold 84 and 14). Published counts are
 * a factual claim on a public page, so they get derived from the real rows.
 */
function countCards(skills) {
  return Object.values(skills || {}).reduce(
    (total, skill) =>
      total + (skill.parts || []).reduce((n, part) => n + (part.cards || []).length, 0),
    0
  );
}

function slugifyPartId(partId) {
  return `redemittel-${String(partId).replace(/_/g, '-')}`;
}

/**
 * One phrase row. Renders as a normal table row from `sm` up and as a stacked
 * card below it — one DOM either way, so the crawler sees each phrase once.
 */
function PhraseRow({ card }) {
  return (
    <tr className="block border-b-2 border-black/10 last:border-b-0 sm:table-row sm:border-b-0 sm:transition-colors sm:duration-300 sm:hover:bg-black/[0.03]">
      <td data-label="Redemittel" className="redemittel-cell block px-4 pt-4 pb-2 align-top sm:table-cell sm:py-3">
        <span className="font-space block font-bold text-black">{card.front}</span>
        {card.exampleGerman && (
          <span className="mt-1 block text-sm italic leading-relaxed text-black/50">
            {card.exampleGerman}
          </span>
        )}
      </td>
      <td data-label="English" className="redemittel-cell block px-4 py-1 align-top sm:table-cell sm:py-3">
        <span className="block text-black/70">{card.back}</span>
        {card.exampleEnglish && (
          <span className="mt-1 block text-sm italic leading-relaxed text-black/40">
            {card.exampleEnglish}
          </span>
        )}
      </td>
      <td data-label="When to use it" className="redemittel-cell block px-4 pt-1 pb-4 align-top sm:table-cell sm:py-3">
        <span className="block text-sm leading-relaxed text-black/60">{card.usage}</span>
        {card.context && (
          <span className="mt-2 inline-block rounded-full border border-black/10 bg-black/5 px-2.5 py-0.5 text-xs font-medium text-black/60">
            {card.context}
          </span>
        )}
      </td>
    </tr>
  );
}

function PartTable({ part }) {
  const cards = part.cards || [];
  if (!cards.length) return null;

  const shown = cards.slice(0, PREVIEW_PER_PART);
  const withheld = cards.length - shown.length;

  return (
    <div className="mt-8 scroll-mt-24" id={slugifyPartId(part.id)}>
      <h4 className="font-space mb-1 flex flex-wrap items-baseline gap-x-3 gap-y-1 text-lg font-bold text-black">
        {part.name}
        <span className="rounded-full bg-black/5 px-2.5 py-0.5 text-xs font-semibold tracking-wide text-black/50">
          {withheld > 0
            ? `${shown.length} of ${cards.length}`
            : `${cards.length} ${cards.length === 1 ? 'phrase' : 'phrases'}`}
        </span>
      </h4>
      {part.description && (
        <p className="mb-4 text-sm leading-relaxed text-black/60 md:text-base">{part.description}</p>
      )}

      <div className="overflow-x-auto rounded-2xl border-2 border-black/10 bg-white shadow-sm">
        <table className="w-full text-left text-sm sm:min-w-[560px]">
          <thead className="hidden sm:table-header-group">
            <tr className="border-b-2 border-black/10 bg-black/5">
              <th className="font-space px-4 py-3 font-bold text-black">Redemittel</th>
              <th className="font-space px-4 py-3 font-bold text-black">English</th>
              <th className="font-space px-4 py-3 font-bold text-black">When to use it</th>
            </tr>
          </thead>
          <tbody className="block sm:table-row-group sm:divide-y sm:divide-black/5">
            {shown.map((card) => (
              <PhraseRow key={card.id} card={card} />
            ))}
          </tbody>
        </table>

        {/* Quiet marker, not a second CTA. One prominent card at the end of the
            section does the asking; repeating it under every table would mean
            four to six shouty CTAs on one page. */}
        {withheld > 0 && (
          <p className="border-t-2 border-dashed border-black/10 bg-black/[0.02] px-4 py-3 text-center text-sm text-black/45">
            + {withheld} more {withheld === 1 ? 'phrase' : 'phrases'} for this task
          </p>
        )}
      </div>
    </div>
  );
}

const RedemittelPhraseTables = ({ provider, level, examName, pdfUrl }) => {
  const lang = useLangParam();
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);

  const fileName = `${provider}-${level}-redemittel.json`.toLowerCase();

  useEffect(() => {
    let cancelled = false;
    fetch(`/data/redemittel/${fileName}`)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((json) => {
        if (!cancelled) setData(json);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [fileName]);

  const skills = useMemo(() => Object.values(data?.skills || {}), [data]);
  const totalCards = useMemo(() => countCards(data?.skills), [data]);
  const allParts = useMemo(() => skills.flatMap((skill) => skill.parts || []), [skills]);
  const shownCards = useMemo(
    () => allParts.reduce((n, part) => n + Math.min((part.cards || []).length, PREVIEW_PER_PART), 0),
    [allParts]
  );

  // The build waits on this attribute before capturing the page, so it must not
  // appear until real rows are mounted. See the header comment.
  const ready = Boolean(data) && totalCards > 0;

  if (failed) {
    return (
      <div className="my-8 flex items-start gap-3 rounded-2xl border-2 border-black/10 bg-black/5 px-5 py-4">
        <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-black/40" />
        <p className="text-sm leading-relaxed text-black/60 md:text-base">
          The phrase list could not be loaded right now.{' '}
          {pdfUrl && (
            <a
              href={pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-black underline underline-offset-2 hover:opacity-80"
            >
              Download the {examName} Redemittel PDF instead
            </a>
          )}
          .
        </p>
      </div>
    );
  }

  if (!ready) {
    // Matches the table shell so the layout does not jump when data lands.
    return (
      <div className="my-8 h-64 animate-pulse rounded-2xl border-2 border-black/10 bg-black/[0.03]" />
    );
  }

  return (
    // No scroll-reveal wrapper here, deliberately. BlogPostPage already reveals the
    // whole <article>, no other post body uses one, and an opacity-0 default would
    // be baked into the prerendered HTML that non-JS crawlers read — on the single
    // most valuable block of content on the site.
    <section data-prerender-ready="true" className="mt-12">
      <h2 className="font-space mb-4 mt-10 text-xl font-bold text-black md:text-2xl">
        {totalCards} {examName} Redemittel, task by task
      </h2>
      <p className="mb-6 text-base leading-relaxed text-black/70 md:text-lg">
        The phrases are grouped the way the exam is: by skill, then by the task they belong to.
        Each one comes with its English meaning, a note on when it earns marks, and a full example
        sentence you can adapt on exam day.{' '}
        {shownCards < totalCards && (
          <>
            The first {PREVIEW_PER_PART} of every task are below — {shownCards} of the {totalCards}{' '}
            in total.
          </>
        )}
      </p>

      {/* Jump nav, matching the pill treatment used elsewhere on the site. */}
      <nav aria-label="Jump to a section" className="mb-10 flex flex-wrap gap-2">
        {allParts.map((part) => (
          <a
            key={part.id}
            href={`#${slugifyPartId(part.id)}`}
            className="
              inline-flex min-h-[40px] items-center rounded-full border-2 border-black/10 bg-white
              px-4 py-2 text-sm font-medium text-black/70 transition-all duration-300
              hover:border-black/30 hover:text-black hover:shadow-md
              focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2
              focus-visible:outline-black active:scale-[0.98]
            "
          >
            {part.name}
            <span className="ml-2 text-xs text-black/40">{(part.cards || []).length}</span>
          </a>
        ))}
      </nav>

      {skills.map((skill) => (
        <div key={skill.name} className="mb-12">
          <div className="mb-2 flex items-center gap-3 border-b-2 border-black/10 pb-3">
            <span aria-hidden="true" className="text-2xl">
              {skill.icon}
            </span>
            <h3 className="font-space text-xl font-bold text-black md:text-2xl">
              {examName} Redemittel: {skill.name}
            </h3>
          </div>
          {(skill.parts || []).map((part) => (
            <PartTable key={part.id} part={part} />
          ))}
        </div>
      ))}

      {/*
        The gate. Styled to match MockExamCTA, which is the established in-blog
        conversion card: black gradient card, #3A86FF eyebrow, font-space, 48px
        targets. Two ways out, because they suit different readers — the PDF for
        someone who wants the list on paper, the account for someone who wants to
        actually learn them.
      */}
      {shownCards < totalCards && (
        <aside
          aria-label={`Get all ${totalCards} ${examName} Redemittel`}
          className="my-10 rounded-2xl border-2 border-black bg-gradient-to-b from-black to-[#1a1a1a] p-6 text-white shadow-lg sm:p-7"
        >
          <p className="font-space mb-2 text-xs font-black uppercase tracking-wider text-[#3A86FF]">
            {totalCards - shownCards} more phrases
          </p>
          <h3 className="font-space mb-2 text-xl font-bold leading-tight sm:text-2xl">
            Get all {totalCards} {examName} Redemittel
          </h3>
          <p className="mb-5 max-w-2xl text-sm text-white/70 sm:text-base">
            You&apos;ve read {shownCards}. The free PDF has the complete list — every phrase, every
            task, with the examples. Or make a free account and drill them with spaced repetition
            until they come out under exam pressure.
          </p>
          <div className="flex flex-col gap-2 sm:flex-row sm:gap-3">
            {pdfUrl && (
              <a
                href={pdfUrl}
                download={pdfUrl.split('/').pop()}
                target="_blank"
                rel="noopener noreferrer"
                className="
                  font-space group inline-flex min-h-[48px] flex-1 items-center justify-center gap-2
                  rounded-xl bg-white px-6 py-3 font-bold text-black transition-all duration-300
                  hover:scale-[1.02] hover:shadow-xl active:scale-[0.98] touch-manipulation
                "
              >
                <FileDown className="h-5 w-5" aria-hidden="true" />
                Download the full PDF
              </a>
            )}
            <Link
              to={`/${lang}/auth?mode=signup`}
              className="
                font-space group inline-flex min-h-[48px] flex-1 items-center justify-center gap-2
                rounded-xl border border-white/20 px-6 py-3 text-sm font-bold text-white/80
                transition-all duration-300 hover:border-white/50 hover:text-white
                active:scale-[0.98] touch-manipulation sm:text-base
              "
            >
              <Repeat className="h-5 w-5" aria-hidden="true" />
              Practise them free
              <ArrowRight
                className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1"
                aria-hidden="true"
              />
            </Link>
          </div>
        </aside>
      )}
    </section>
  );
};

export default RedemittelPhraseTables;
